/**
 * Account switcher / current-account display helpers.
 * Prefer canonical portal/account_type over legacy membership account_id.
 */

const SELF_REPAIR_ROLES = new Set([
  'self_repair',
  'self_repair_customer',
  'self_repair_user',
  'premium_user',
]);

const AGENCY_ROLES = new Set([
  'agency_owner',
  'agency_admin',
  'agency_staff',
  'agency_manager',
  'agency_user',
  'owner',
  'admin',
  'staff',
  'specialist',
  'team_member',
  'employee',
]);

export function resolveAccountDisplayType(account) {
  if (!account) return null;
  const portal = String(account.portal || account.portal_type || '').toLowerCase();
  const type = String(
    account.account_type || account.type || ''
  ).toLowerCase();
  const role = String(account.effective_role || account.role || '').toLowerCase();

  if (portal === 'self_repair' || type === 'self_repair' || SELF_REPAIR_ROLES.has(role)) {
    return 'self_repair';
  }
  if (portal === 'platform' || type === 'platform') return 'platform';
  if (portal === 'funding_hq' || type === 'funding_hq') return 'funding_hq';
  if (
    portal === 'agency'
    || portal === 'agency_staff'
    || type === 'agency'
    || type === 'subaccount'
    || AGENCY_ROLES.has(role)
  ) {
    return 'agency';
  }
  return type || portal || null;
}

export function resolveAccountDisplayRole(account) {
  if (!account) return null;
  if (account.role_label) return account.role_label;
  const role = String(account.effective_role || account.role || '').toLowerCase();
  if (SELF_REPAIR_ROLES.has(role)) return 'Self Repair Customer';
  if (role === 'agency_owner' || role === 'owner') return 'Agency Owner';
  if (role === 'agency_admin' || role === 'admin') return 'Agency Admin';
  if (role === 'agency_staff' || role === 'staff' || role === 'specialist') return 'Agency Staff';
  if (role === 'agency_manager') return 'Agency Manager';
  return account.effective_role || account.role || null;
}

/** Subtitle under account name, e.g. "self_repair · Self Repair Customer". */
export function formatAccountSwitcherSubtitle(account) {
  const type = resolveAccountDisplayType(account);
  const roleLabel = resolveAccountDisplayRole(account);
  return [type, roleLabel].filter(Boolean).join(' · ');
}

/**
 * Agency id for routing — never use self_repair membership account_id.
 */
export function resolveAgencyIdForRouting(accountOrUser) {
  if (!accountOrUser) return null;
  const type = resolveAccountDisplayType(accountOrUser);
  if (type === 'self_repair' || type === 'platform') return null;
  return (
    accountOrUser.agency_id
    || (
      type === 'agency' || type === 'funding_hq' || type === 'subaccount'
        ? accountOrUser.account_id
        : null
    )
    || null
  );
}

export function isAgencyEligibleRole(role) {
  return AGENCY_ROLES.has(String(role || '').toLowerCase());
}

export function isSelfRepairEligibleRole(role) {
  return SELF_REPAIR_ROLES.has(String(role || '').toLowerCase());
}
